/**
 * Arte do tema Coração da Morena (gerada no Higgsfield, preparada por art/tools/process.py).
 * Os arquivos ficam em public/art/morena e são servidos pelo próprio Worker.
 */
import { SQUARES } from '@tigrinho/engine';

const BASE = '/art/morena/';
const a = (name: string) => `${BASE}${name}.webp`;

const PAWN_SLUGS = ['0-jegue', '1-pombo', '2-vo', '3-caramelo', '4-galo', '5-tia'] as const;

export interface PawnArt {
  idle: string;
  happy: string;
  sad: string;
}

export const PAWN_ART: PawnArt[] = PAWN_SLUGS.map((s) => ({
  idle: a(`peao-${s}`),
  happy: a(`reacao-${s}-feliz`),
  sad: a(`reacao-${s}-triste`),
}));

export const pawnArt = (pawn: number): PawnArt => PAWN_ART[pawn] ?? PAWN_ART[0]!;

/** Ícone dos cantos, resorts, imposto e casas de carta (as cidades mostram o prédio). */
const SPECIAL: Record<number, string> = {
  0: 'casa-inicio',
  5: 'casa-jegue-turbo',
  8: 'casa-rede',
  13: 'casa-bicicleta',
  16: 'casa-carro-de-som',
  21: 'casa-barco',
  24: 'casa-disco-voador',
  27: 'casa-imposto',
  29: 'casa-torre-wifi',
};

export function squareIcon(index: number): string | null {
  const sq = SQUARES[index];
  if (!sq) return null;
  if (sq.kind === 'chance') return a('casa-sorte');
  const name = SPECIAL[index];
  return name ? a(name) : null;
}

/**
 * Prédio de uma cidade por nível: 1 a 3 casas e 4 = hotel.
 * (A bandeirola, nível 0, é desenhada na própria tela, na cor do dono.)
 */
export function buildingArt(group: number, level: number): string {
  const art = level >= 4 ? 5 : Math.min(3, Math.max(1, level));
  return a(`predio-g${group}-${art}`);
}

export const CARD_ART: Record<'sorte' | 'reves' | 'caos', string> = {
  sorte: a('carta-sorte'),
  reves: a('carta-reves'),
  caos: a('carta-caos'),
};

/** Itens do arsenal que têm arte própria (os outros continuam emoji gigante). */
export const ITEM_ART: Partial<Record<string, { item: string; splat?: string }>> = {
  tomate: { item: a('item-tomate'), splat: a('respingo-tomate') },
  ovo: { item: a('item-ovo'), splat: a('respingo-ovo') },
  torta: { item: a('item-torta'), splat: a('respingo-torta') },
  chinelo: { item: a('item-chinelo') },
  boleto: { item: a('casa-boleto') },
};

export const ART = {
  logo: a('logo-tigrinho'),
  city: a('fundo-cidade'),
  trophy: a('casa-trofeu'),
  money: a('item-dinheiro'),
  soundCar: a('casa-carro-de-som'),
  ufo: a('casa-disco-voador'),
  boleto: a('casa-boleto'),
  chest: a('casa-sorte'),
  jail: a('casa-cadeia'),
  /** Carta do "Faz o L" (Governo do Amor). */
  love: a('carta-amor'),
  /** Turma do MST (boné e bandeira vermelhos). */
  mst: a('mst-turma'),
  /** Fazendeiro com a liminar e o cachorro bravo. */
  farmer: a('fazendeiro'),
};

/** Lista de tudo, para pré-carregar enquanto a galera está no lobby. */
export function allArt(): string[] {
  const list = new Set<string>(Object.values(ART));
  for (const p of PAWN_ART) list.add(p.idle).add(p.happy).add(p.sad);
  for (const sq of SQUARES) {
    const icon = squareIcon(sq.index);
    if (icon) list.add(icon);
  }
  for (let g = 1; g <= 8; g++) for (let l = 1; l <= 4; l++) list.add(buildingArt(g, l));
  for (const c of Object.values(CARD_ART)) list.add(c);
  for (const it of Object.values(ITEM_ART)) {
    if (it) {
      list.add(it.item);
      if (it.splat) list.add(it.splat);
    }
  }
  return [...list];
}

let preloaded = false;
/** Baixa a arte em segundo plano (o navegador guarda em cache). */
export function preloadArt(): void {
  if (preloaded || typeof Image === 'undefined') return;
  preloaded = true;
  const list = allArt();
  let i = 0;
  const next = () => {
    const src = list[i++];
    if (!src) return;
    const img = new Image();
    img.decoding = 'async';
    img.onload = img.onerror = next;
    img.src = src;
  };
  // Algumas em paralelo, sem travar a tela.
  for (let k = 0; k < 4; k++) next();
}
